import { useLang } from "@/lib/i18n";

const DOCS = {
  terms: {
    title: "Terms of Service (CGU / CGV)",
    sections: [
      ["1. Service", "Soundra is a hosting platform enabling independent artists to publish audio and listeners to stream it. Editorial hubs (e.g. “100% Women in Music”) are optional showcase channels and never restrict access to core services, distribution or monetization for any user."],
      ["2. Uploads & rights", "By uploading, you confirm you hold 100% of the master and publishing rights (or valid licences). Starter accounts may keep 5 active tracks; all accounts are capped at 20 uploads per 24h and 200MB per file."],
      ["3. Recurring Subscription Terms", "Rising Star and Elite Label renew automatically each month or year until cancelled in the Customer Portal. Upgrades and downgrades are prorated. Annual plans are billed upfront. Prices include applicable VAT where required. EU consumers may withdraw within 14 days unless the digital service was fully used with express consent."],
      ["4. Tips & payouts", "Tips are processed by Stripe. Payouts to artists require completed identity (KYC) and bank verification via Stripe Connect; until then funds are held."],
      ["5. Moderation", "Automated tools may flag tag inconsistencies but never ban automatically. Artists may request human review at any time. Removal from an editorial hub is a re-classification and does not affect account status."],
    ],
  },
  privacy: {
    title: "Privacy Policy (RGPD / GDPR)",
    sections: [
      ["Controller", "Soundra Music SAS, Paris — dpo@soundra.fm."],
      ["Data we process", "Account data, uploaded content, listening events (de-duplicated, aggregated for charts), consent choices, and billing references (card data is handled exclusively by Stripe)."],
      ["Legal bases", "Contract performance, legitimate interest (fraud prevention, rate limiting), and consent (analytics, persistent storage)."],
      ["Your rights", "Access, portability (JSON export), rectification, erasure (“Right to be Forgotten”), objection and human review of automated decisions. Use /settings/privacy or contact the DPO. You may complain to the CNIL."],
      ["Retention", "Account data while active; deleted accounts are anonymised immediately; invoices kept 10 years as required by French law."],
    ],
  },
  legal: {
    title: "Legal Notices (Mentions Légales)",
    sections: [
      ["Publisher", "Soundra — Contact: soundra.help@outlook.com"],
      ["Publication director", "Le Président de Soundra"],
      ["Hosting status (Hébergeur)", "For user-generated content, Soundra acts as a hosting provider under article 6-I-2 of the LCEN and the EU Digital Services Act. Notified illicit content (incl. DMCA / copyright claims) is reviewed with priority and suspended within 24 hours where justified."],
      ["Technical host", "Cloud infrastructure provider, EU region."],
    ],
  },
  legal_fr: {
    title: "Mentions Légales",
    eyebrow: "Mentions légales",
    updated: "Dernière mise à jour : juin 2026",
    sections: [
      ["Éditeur", "Soundra — Contact : soundra.help@outlook.com"],
      ["Directeur de la publication", "Le Président de Soundra"],
      ["Statut d'hébergeur", "Pour les contenus publiés par les utilisateurs, Soundra agit en qualité d'hébergeur au sens de l'article 6-I-2 de la LCEN et du règlement européen sur les services numériques (DSA). Les contenus illicites notifiés (y compris les réclamations DMCA / droits d'auteur) sont examinés en priorité et suspendus sous 24 heures lorsque cela est justifié."],
      ["Hébergeur technique", "Fournisseur d'infrastructure cloud, région Union européenne."],
    ],
  },
};

export default function Legal({ doc }) {
  const lang = useLang((s) => s.lang);
  const d = (lang === "fr" && DOCS[`${doc}_fr`]) || DOCS[doc];
  return (
    <article data-testid={`legal-page-${doc}`} className="max-w-3xl reveal">
      <div className="eyebrow">{d.eyebrow || "Legal"}</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2">{d.title}</h1>
      <p className="text-xs font-mono text-zinc-500 mt-2">{d.updated || "Last updated: June 2026"}</p>
      <div className="mt-10 space-y-8">{d.sections.map(([h, p]) => <section key={h}><h2 className="font-display text-xl font-bold">{h}</h2><p className="text-zinc-400 mt-2 leading-relaxed">{p}</p></section>)}</div>
    </article>
  );
}
